import { useEffect, useCallback, useState } from 'react';

import { IntegratedFreightShippingData } from './IntegratedFreightShippingFragment.props';
import { buildFreightActions } from './useIntegratedFreightShippingActions';
import {
  CourierData,
  DispatchDetail,
  QuoteFreightOrderTypeResponse,
} from '../../../../../@types/shipment.type';
import { NodeAttributesReponse } from '../../../../../@types/structure.type';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu';
import {
  FreightPackageType,
  SelectedShipmentPackage,
} from '../../../../../components/molecules/ShipmentPackagesList';
import { AddressDataType } from '../../../../../components/organisms/StructureAddress';
import { NodeAttributeType } from '../../../../../configs/enums';
import {
  AUSPOST_STARTRACK_COURIER_NAME,
  INTEGRATED_FREIGHT_SHIPPING_DATA,
} from '../../../../../constants/shipment';
import {
  useArchiveFreightOrder,
  useCreateFreightOrder,
  useGetFreightOrdersByShipment,
  useGetFreightPackageTypes,
  useQuoteFreightOrder,
  useReturnFreightOrder,
} from '../../../../../queries/freight-query';
import { useGetStateWithCountryData } from '../../../../../queries/geosocial-query';
import {
  useGetDestinationAddressByNodeId,
  useGetNodeAttributesByNodeId,
  useGetNodeAttributesByPropertyTypeAndNodeIds,
} from '../../../../../queries/structure-query';
import { getCourierIds, getFreightIntegrationEnabledCouriers } from '../../../../../utils/shipment';

interface UseIntegratedFreightShippingArgs {
  shipment: DispatchDetail;
  couriers: CourierData[];
}

export const useIntegratedFreightShipping = ({
  shipment,
  couriers,
}: UseIntegratedFreightShippingArgs) => {
  const [freightEnabledCouriers, setFreightEnabledCouriers] = useState<CourierData[]>([]);
  const [serviceTypes, setServiceTypes] = useState<DropdownItem[]>([]);
  const [freightPackageTypes, setFreightPackageTypes] = useState<FreightPackageType[]>([]);
  const [integratedFreightShippingData, setIntegratedFreightShippingData] =
    useState<IntegratedFreightShippingData>(INTEGRATED_FREIGHT_SHIPPING_DATA);
  const [destinationAddress, setDestinationAddress] = useState<AddressDataType>();
  const [selectedOption, setSelectedOption] = useState<string>();
  const [destinationAddressOptions, setDestinationAddressOptions] = useState<DropdownItem[]>([]);
  const [structureAddressDataset, setStructureAddressDataset] = useState<AddressDataType[]>([]);
  const [addressData, setAddressData] = useState<AddressDataType[]>([]);
  const [editAddressData, setEditAddressData] = useState<AddressDataType>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; the address form shape isn't typed at the source.
  const [addressForm, setAddressForm] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [quoteData, setQuoteData] = useState<QuoteFreightOrderTypeResponse[]>([]);
  // Bumped whenever a freight action completes. Freight orders refetch off this
  // and the card re-keys on it; it used to be the toast message doing both jobs.
  const [freightActionCount, setFreightActionCount] = useState(0);
  const signalFreightAction = useCallback(() => setFreightActionCount((n) => n + 1), []);

  const getNodeAttributesByPropertyTypeAndNodeIds = useGetNodeAttributesByPropertyTypeAndNodeIds(
    NodeAttributeType.FreightIntegrationEnabled,
    getCourierIds(couriers)
  );
  const getNodeAttributesByNodeId = useGetNodeAttributesByNodeId(
    integratedFreightShippingData.shippingProviderId
  );
  const getFreightPackageTypes = useGetFreightPackageTypes();
  const createFreightOrder = useCreateFreightOrder(shipment.id);
  const archiveFreightOrder = useArchiveFreightOrder(shipment.id);
  const returnFreightOrder = useReturnFreightOrder(shipment.id);
  const quoteFreightOrder = useQuoteFreightOrder();
  const getFreightOrdersByShipment = useGetFreightOrdersByShipment(shipment.id || 0);
  const getContactByNodeId = useGetNodeAttributesByNodeId(shipment?.destinationNodeId);
  const getStateWithCountryData = useGetStateWithCountryData(destinationAddress?.stateId || 0);
  const { data: destinationAddressdata, refetch: refetchDestinationNode } =
    useGetDestinationAddressByNodeId(shipment?.destinationNodeId.toString() || '');

  useEffect(() => {
    getNodeAttributesByPropertyTypeAndNodeIds.data &&
      setFreightEnabledCouriers(
        getFreightIntegrationEnabledCouriers(
          couriers,
          getNodeAttributesByPropertyTypeAndNodeIds.data
        )
      );
  }, [getNodeAttributesByPropertyTypeAndNodeIds.data, couriers]);

  useEffect(() => {
    if (!getNodeAttributesByNodeId.data) return;
    const types: DropdownItem[] = [];
    for (const item of getNodeAttributesByNodeId.data as NodeAttributesReponse[]) {
      if (item.propertyTypeCode === NodeAttributeType.CourierServiceTypes) {
        types.push({ value: item.value.code, label: item.value.name });
      }
    }
    setServiceTypes(types);
  }, [getNodeAttributesByNodeId.data]);

  useEffect(() => {
    getFreightPackageTypes.data && setFreightPackageTypes(getFreightPackageTypes.data);
  }, [getFreightPackageTypes.data]);

  useEffect(() => {
    refetchDestinationNode();
  }, [shipment.destinationNodeId, refetchDestinationNode]);

  useEffect(() => {
    if (destinationAddressdata?.length === 0) {
      setIntegratedFreightShippingData((prev) => ({ ...prev, isCustomAddress: true }));
    } else {
      const options: { value: string; label: string; addressLabel: AddressDataType }[] = [];
      destinationAddressdata?.forEach((obj) => {
        options.push({
          value: obj.id.toString(),
          label: `${obj.value.line1}, ${obj.value?.line2 ? obj.value?.line2 + ',' : ''} ${obj.value.suburb}, ${obj.value.stateName}, ${obj.value.postCode}, ${obj.value.countryName}`,
          addressLabel: obj.value,
        });
      });
      destinationAddressdata && setSelectedOption(destinationAddressdata[0].id.toString());
      setDestinationAddressOptions(options);
    }
  }, [destinationAddressdata]);

  useEffect(() => {
    if (structureAddressDataset.length > 0) {
      setDestinationAddress(structureAddressDataset[0]);
    } else if (selectedOption && destinationAddressdata) {
      const match = destinationAddressdata.find((item) => item.id === parseInt(selectedOption, 10));
      if (match) {
        setDestinationAddress(match.value);
      }
    }
    getContactByNodeId.refetch();
    getStateWithCountryData.refetch();
  }, [selectedOption, structureAddressDataset]);

  useEffect(() => {
    getStateWithCountryData.refetch();
  }, [destinationAddress]);

  useEffect(() => {
    getFreightOrdersByShipment.refetch();
  }, [freightActionCount]);

  useEffect(() => {
    const ausCourier = freightEnabledCouriers?.find(
      (c) => c?.name === AUSPOST_STARTRACK_COURIER_NAME
    );
    if (ausCourier && integratedFreightShippingData?.shippingProviderId === 0) {
      setIntegratedFreightShippingData((prev) => ({ ...prev, shippingProviderId: ausCourier.id }));
    }
  }, [freightEnabledCouriers]);

  const mainOrder = getFreightOrdersByShipment.data?.find((item) => !item.isReturnOrder);
  const returnedOrder = getFreightOrdersByShipment.data?.find((item) => item.isReturnOrder);
  const ordersData = {
    mainId: mainOrder?.id,
    mainOrderId: mainOrder?.orderId,
    mainOrderNumber: mainOrder?.orderNumber,
    mainOrderShowArchived: mainOrder?.showArchived,
    returnedId: returnedOrder?.id,
    returnedOrderId: returnedOrder?.orderId,
    returnedOrderNumber: returnedOrder?.orderNumber,
    returnedOrderShowArchived: returnedOrder?.showArchived,
  };

  const onPackageSelect = (selectedPackages: SelectedShipmentPackage[]) =>
    setIntegratedFreightShippingData((prev) => ({ ...prev, selectedPackages }));

  const actions = buildFreightActions({
    shipment,
    data: integratedFreightShippingData,
    serviceTypes,
    contactData: getContactByNodeId.data,
    destinationAddress,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- destination address response shape is untyped at the API source; safe because buildFreightActions only reads shallow `.find()` results.
    destinationAddressdata: destinationAddressdata as unknown as any[] | undefined,
    stateShortName: getStateWithCountryData.data?.shortName,
    ordersData,
    createFreightOrder: (payload) => createFreightOrder.mutateAsync(payload),
    archiveFreightOrder: (payload) => archiveFreightOrder.mutateAsync(payload),
    returnFreightOrder: (payload) => returnFreightOrder.mutateAsync(payload),
    quoteFreightOrder: (payload) =>
      quoteFreightOrder.mutateAsync(payload as Parameters<typeof quoteFreightOrder.mutateAsync>[0]),
    signalFreightAction,
    setQuoteData,
    setOpenModal,
    onPackageSelect,
  });

  const isPending =
    createFreightOrder.isPending ||
    archiveFreightOrder.isPending ||
    returnFreightOrder.isPending ||
    quoteFreightOrder.isPending;

  return {
    state: {
      freightActionCount,
      integratedFreightShippingData,
      setIntegratedFreightShippingData,
      freightEnabledCouriers,
      serviceTypes,
      freightPackageTypes,
      destinationAddress,
      setDestinationAddress,
      selectedOption,
      setSelectedOption,
      destinationAddressOptions,
      structureAddressDataset,
      setStructureAddressDataset,
      addressData,
      setAddressData,
      editAddressData,
      setEditAddressData,
      addressForm,
      setAddressForm,
      openModal,
      setOpenModal,
      quoteData,
    },
    derived: {
      ordersData,
      isPending,
      destinationAddressdata,
    },
    actions,
  };
};
